import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./Modal.module.css";
import { Button } from "./Button";

const FOCUSABLE = "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

export function Modal({ open, onClose, title, children, footer, size = "md" }) {
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);

  // onClose is passed as an inline arrow by essentially every caller
  // (`onClose={() => setThingOpen(false)}`), so its identity changes on every
  // render of the parent. Holding it in a ref lets the effect below depend on
  // [open] alone.
  //
  // It previously depended on [open, onClose], which made typing inside a
  // modal impossible: each keystroke re-rendered the parent, produced a new
  // onClose, and tore down and re-ran the effect -- whose cleanup restores
  // focus and whose body then focuses the first focusable element, the header
  // close button. One character and focus jumped to Close. That affected every
  // modal with a text input in this app and in admin/, not just the one it was
  // reported against (the hive note modal).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;

    // Only take focus if nothing inside the dialog has it already. React
    // applies a child's autoFocus during the commit, before effects run, so
    // focusing unconditionally here would override it -- which is exactly
    // what happened to the note textarea's autoFocus.
    if (!dialogRef.current?.contains(document.activeElement)) {
      dialogRef.current?.querySelector(FOCUSABLE)?.focus();
    }

    function handleKeyDown(e) {
      // Via the ref: this listener is registered once per open, so a captured
      // onClose would go stale.
      if (e.key === "Escape") onCloseRef.current?.();
      if (e.key === "Tab") {
        const focusables = dialogRef.current?.querySelectorAll(FOCUSABLE);
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        className={[styles.dialog, styles[size]].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <header className={styles.header}>
          <h2 id="modal-title" className={styles.title}>{title}</h2>
          <Button variant="ghost" size="sm" aria-label="Close dialog" onClick={onClose}>
            ✕
          </Button>
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body
  );
}
